import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EMoneySource, publishOffer, setPlacingMoneySource } from '../../redux/reducers';
import { selectAnalyticsFormId, selectAnalyticsOfferStatus, selectAnalyticsProduct } from '../../selectors/analytics';
import { selectCurrentBet } from '../../selectors/auction';
import { selectBetPosition } from '../../selectors/betPosition';
import {
  selectTotalAmount,
  selectIsPublishButtonPending,
  selectPublicationButtonText,
  selectIsPublishButtonDisabled,
  selectIsAutoProlongChecked,
} from '../../selectors/checkoutInfo';
import { selectIsShortForm } from '../../selectors/layout';
import { selectOfferId } from '../../selectors/offer';
import { selectShouldPayByCard } from '../../selectors/publishAvailable';
import { selectActiveServiceId, selectIsSubagentLimitExceed } from '../../selectors/publishTerms';
import { selectPublicationButtonConfig } from '../../selectors/publication';
import { IAdaptiveSettings } from '../../types/adaptiveSettings';
import { getAnalyticsSiteType } from '../../utils';
import { useApplicationContext } from '../ApplicationContext';
import { PublishButton } from './PublishButton';
import { trackPublishByMoneyFailed, trackPublishClick } from './tracking';

export const PublishButtonContainer = () => {
  const { config, servicePickerWidgetMicrofrontend } = useApplicationContext();

  const totalPrice = useSelector(selectTotalAmount);
  const isPending = useSelector(selectIsPublishButtonPending);
  const isShortForm = useSelector(selectIsShortForm);
  const analyticsOfferStatus = useSelector(selectAnalyticsOfferStatus);
  const text = useSelector(selectPublicationButtonText);
  const shouldPayByCard = useSelector(selectShouldPayByCard);
  const offerId = useSelector(selectOfferId);
  const isDisabled = useSelector(selectIsPublishButtonDisabled);
  const currentBet = useSelector(selectCurrentBet);
  const analyticsProduct = useSelector(selectAnalyticsProduct);
  const betPosition = useSelector(selectBetPosition);
  const selectedServiceId = useSelector(selectActiveServiceId)!;
  const isAutoprolong = useSelector(selectIsAutoProlongChecked)!;
  const isSubagentLimitExceed = useSelector(selectIsSubagentLimitExceed)!;
  const formId = useSelector(selectAnalyticsFormId);
  const publicationButtonConfig = useSelector(selectPublicationButtonConfig);

  const { offerPosition = null, page = null } = betPosition || {};

  const { deviceType } = config.get<IAdaptiveSettings>('adaptiveSettings') || {};

  const siteType = getAnalyticsSiteType(deviceType);

  const dispatch = useDispatch();

  const handleButtonClick = React.useCallback(async () => {
    if (isSubagentLimitExceed) {
      dispatch(setPlacingMoneySource(EMoneySource.Self));

      return;
    }

    if (isDisabled) {
      return;
    }

    trackPublishClick({
      analyticsOfferStatus,
      analyticsProduct,
      totalPrice: totalPrice.price,
      currentBet,
      selectedServiceId,
      offerPosition,
      page,
      isAutoprolong,
      formId,
    });

    if (publicationButtonConfig.shouldSwitchToSelf) {
      servicePickerWidgetMicrofrontend?.switchToSelfMoneySource();

      return;
    }

    const isPublishAllowed = await servicePickerWidgetMicrofrontend?.checkPublishAvailability();

    if (!isPublishAllowed) {
      trackPublishByMoneyFailed({ offerId, isShortForm, siteType, analyticsOfferStatus });

      return;
    }

    dispatch(publishOffer({ isPremoderationSkip: false }));
  }, [
    dispatch,
    totalPrice,
    isShortForm,
    analyticsOfferStatus,
    siteType,
    shouldPayByCard,
    offerId,
    isDisabled,
    currentBet,
    selectedServiceId,
    offerPosition,
    page,
    isAutoprolong,
    isSubagentLimitExceed,
    analyticsProduct,
    formId,
    publicationButtonConfig,
    servicePickerWidgetMicrofrontend,
  ]);

  return <PublishButton text={text} handleButtonClick={handleButtonClick} isPending={isPending} />;
};
